import React, { useEffect, useMemo, useState } from "react";
import { Activity, ShieldCheck, Store, Users } from "lucide-react";
import MainLayout from "../components/CommonComponents/MainLayout";
import EmptyState from "../components/CommonComponents/EmptyState";
import { fetchCustomers } from "../services/customerApi";

export default function SuperAdminDashboard() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadDashboard = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetchCustomers();
        if (!isMounted) return;
        setCustomers(Array.isArray(response.data) ? response.data : []);
      } catch (fetchError) {
        console.error("Failed to load superadmin dashboard:", fetchError);
        if (isMounted) setError("Failed to load dashboard data.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadDashboard();

    return () => {
      isMounted = false;
    };
  }, []);

  const stats = useMemo(() => {
    const activeCustomers = customers.filter((customer) => customer.status === "ACTIVE").length;
    const inactiveCustomers = customers.filter((customer) => customer.status === "INACTIVE").length;
    const posCustomers = customers.filter((customer) => customer.system_type).length;

    return [
      { label: "Customers", value: customers.length, hint: "Registered customer accounts", icon: Users },
      { label: "Active", value: activeCustomers, hint: "Currently enabled tenants", icon: Activity },
      { label: "Inactive", value: inactiveCustomers, hint: "Paused or disabled tenants", icon: ShieldCheck },
      { label: "POS Tenants", value: posCustomers, hint: "Customers using POS systems", icon: Store },
    ];
  }, [customers]);

  return (
    <MainLayout>
      <div className="px-4 pt-6 sm:px-6">
        <section className="relative overflow-hidden rounded-[30px] border border-slate-200/85 bg-[linear-gradient(145deg,#fffafa,#fffdfd,#ffffff)] p-5 shadow-[0_24px_80px_rgba(15,23,42,0.08)] sm:p-6">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full blur-3xl" style={{ background: "color-mix(in srgb, var(--color-primary) 18%, transparent)" }} />
            <div className="absolute left-10 top-0 h-28 w-28 rounded-full bg-white/60 blur-2xl" />
          </div>

          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/70 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 shadow-sm">
              <ShieldCheck className="h-3.5 w-3.5" />
              Platform Overview
            </div>
            <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
              Super Admin Dashboard
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500 sm:text-[15px]">
              Monitor platform-wide customer status and use the Customers tab to manage customer records.
            </p>
          </div>

          {error ? (
            <EmptyState
              icon={ShieldCheck}
              title="Failed to Load Dashboard"
              message={error}
              size="default"
            />
          ) : (
            <div className="relative mt-6 grid grid-cols-2 gap-2 xl:grid-cols-4">
              {(loading ? Array.from({ length: 4 }) : stats).map((item, index) => {
                if (loading) {
                  return (
                    <div key={index} className="rounded-[24px] border border-white/70 bg-white/80 p-4 shadow-[0_8px_18px_rgba(15,23,42,0.06)]">
                      <div className="h-3 w-24 rounded-full bg-slate-100" />
                      <div className="mt-3 h-8 w-16 rounded-full bg-slate-200" />
                      <div className="mt-2 h-3 w-32 rounded-full bg-slate-100" />
                    </div>
                  );
                }

                const Icon = item.icon;
                return (
                  <div key={item.label} className="rounded-[24px] border border-white/70 bg-white/80 p-4 shadow-[0_8px_18px_rgba(15,23,42,0.06)]">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                      <Icon className="h-4 w-4" />
                      {item.label}
                    </div>
                    <p className="mt-3 truncate text-2xl font-semibold text-slate-900">{item.value}</p>
                    <p className="mt-1 truncate text-xs text-slate-500">{item.hint}</p>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </MainLayout>
  );
}
